import React, { useRef, useState } from 'react';
import {
  canUseProvider,
  getProviderHint,
  getBasemapFamily,
  getEnabledBasemapFamilies,
  isMandatoryOverlay
} from '../utils/mapProviderSettings';

/**
 * 叠加层勾选框状态（纯函数，便于单测）。
 * 必显叠加层（国界线）：可用时恒勾选、恒禁用（置灰，不可关闭）；不可用（缺 Key）时不勾选、禁用并提示。
 */
export function getOverlayCheckboxState(item, activeOverlayIds = [], usable = true, hint = '') {
  if (isMandatoryOverlay(item?.id)) {
    return {
      checked: !!usable,
      disabled: true,
      locked: true,
      title: usable ? '国界线为必显图层，随底图自动加载并纠偏，不可关闭' : (hint || '国界线暂不可用')
    };
  }
  const active = activeOverlayIds.includes(item?.id);
  return { checked: active, disabled: !usable && !active, locked: false, title: hint };
}

/**
 * 标题行「立体」滑动开关：只控制 DEM 山峰，不影响标注/等高线
 */
function TerrainSlideSwitch({ on = false, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle?.();
      }}
      title={on ? '关闭立体山峰（标注、等高线不受影响）' : '开启立体山峰'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        flexShrink: 0
      }}
    >
      <span style={{ fontSize: 12, color: '#666' }}>立体山峰</span>
      <span
        style={{
          width: 32,
          height: 18,
          borderRadius: 9,
          background: on ? '#1976d2' : '#cfd8dc',
          position: 'relative',
          display: 'inline-block',
          transition: 'background 0.15s ease',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.06)'
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,.25)',
            transition: 'left 0.15s ease'
          }}
        />
      </span>
    </button>
  );
}

/**
 * 指挥页底图 / 叠加层选择按钮
 * - 鼠标移上图标：展开菜单
 * - 移出图标与菜单区域：收起（未固定时）
 * - 点击图标：固定 / 取消固定显示
 */
export default function BasemapLayerControl({
  compact = false,
  showPanel,
  onTogglePanel,
  enabledBasemaps: _enabledBasemaps = [],
  enabledOverlays = [],
  mapProviderSettings,
  currentBasemapId,
  activeOverlayIds = [],
  onSwitchBasemap,
  onToggleOverlay,
  terrain3dOn = false,
  onToggleTerrain3d
}) {
  const [hovered, setHovered] = useState(false);
  const leaveTimerRef = useRef(null);
  const pinned = !!showPanel;
  const open = pinned || hovered;

  const families = getEnabledBasemapFamilies(mapProviderSettings);
  const currentFamily = getBasemapFamily(currentBasemapId);
  const currentLabel = families.find((f) => f.family === currentFamily)?.label
    || currentBasemapId
    || '未选择';

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const handleEnter = () => {
    clearLeaveTimer();
    setHovered(true);
  };

  const handleLeave = () => {
    clearLeaveTimer();
    // 短延迟，避免移向菜单时闪断
    leaveTimerRef.current = setTimeout(() => {
      setHovered(false);
      leaveTimerRef.current = null;
    }, 160);
  };

  const handleTogglePin = () => {
    clearLeaveTimer();
    onTogglePanel?.();
  };

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        onClick={handleTogglePin}
        title={
          pinned
            ? `当前底图：${currentLabel}（已固定，再次点击取消固定）`
            : `当前底图：${currentLabel}（悬停展开，点击固定）`
        }
        aria-label={`当前底图：${currentLabel}`}
        aria-expanded={open}
        aria-pressed={pinned}
        style={{
          width: compact ? 42 : 48,
          height: compact ? 42 : 48,
          borderRadius: 12,
          border: pinned ? '2px solid #1976d2' : '1px solid #d8d8d8',
          background: pinned ? '#e3f2fd' : '#fff',
          cursor: 'pointer',
          boxShadow: '0 1px 4px rgba(0,0,0,.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0
        }}
      >
        <svg width={compact ? 22 : 26} height={compact ? 22 : 26} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 10.2L4.2 14.1L12 18L19.8 14.1L12 10.2Z" fill="#d9d9d9" stroke="#9e9e9e" strokeWidth="1" strokeLinejoin="round" />
          <path d="M12 6.6L4.2 10.5L12 14.4L19.8 10.5L12 6.6Z" fill="#ececec" stroke="#9e9e9e" strokeWidth="1" strokeLinejoin="round" />
          <path d="M12 3L4.2 6.9L12 10.8L19.8 6.9L12 3Z" fill="#fafafa" stroke="#9e9e9e" strokeWidth="1" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: 8,
            background: '#fff',
            border: '1px solid #cfd8dc',
            borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0,0,0,.2)',
            padding: '8px 12px',
            zIndex: 40,
            minWidth: 228,
            maxHeight: compact ? 260 : 400,
            overflowY: 'auto'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 5
          }}
          >
            <div style={{ fontSize: 12, color: '#666' }}>底图</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {typeof onToggleTerrain3d === 'function' && (
                <TerrainSlideSwitch on={terrain3dOn} onToggle={onToggleTerrain3d} />
              )}
              {pinned && (
                <span style={{ fontSize: 11, color: '#1976d2' }}>已固定</span>
              )}
            </div>
          </div>
          {families.map((group) => {
            const usable = group.memberIds.some((id) => canUseProvider(id, mapProviderSettings));
            const seedId = group.memberIds.find((id) => canUseProvider(id, mapProviderSettings))
              || group.seedId;
            return (
              <label
                key={group.family}
                title={getProviderHint(seedId, mapProviderSettings)}
                style={{ display: 'flex', gap: 6, padding: '3px 0', fontSize: 13, opacity: usable ? 1 : 0.45 }}
              >
                <input
                  type="radio"
                  name="zhxt-basemap"
                  checked={currentFamily === group.family}
                  disabled={!usable}
                  onChange={() => onSwitchBasemap?.(seedId)}
                />
                {group.label}
              </label>
            );
          })}
          <div style={{ borderTop: '1px solid #e0e0e0', margin: '6px 0' }} />
          <div style={{ fontSize: 12, color: '#666', marginBottom: 5 }}>标注叠加</div>
          {enabledOverlays.map((item) => {
            const usable = item.type === 'placeholder' || canUseProvider(item.id, mapProviderSettings);
            const cb = getOverlayCheckboxState(
              item,
              activeOverlayIds,
              usable,
              getProviderHint(item.id, mapProviderSettings)
            );
            return (
              <label
                key={item.id}
                title={cb.title}
                style={{
                  display: 'flex',
                  gap: 6,
                  padding: '3px 0',
                  fontSize: 13,
                  opacity: usable ? 1 : 0.45,
                  ...(cb.locked ? { color: '#999', cursor: 'not-allowed' } : {})
                }}
              >
                <input
                  type="checkbox"
                  checked={cb.checked}
                  disabled={cb.disabled}
                  data-mandatory={cb.locked ? 'true' : undefined}
                  onChange={() => { if (!cb.locked) onToggleOverlay?.(item.id); }}
                />
                {item.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
